import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Menu, X, ChevronRight, Check, Star, MapPin, Gift, Calendar, User, Car, FileText, DollarSign, CreditCard, Phone, Mail, Clock } from 'lucide-react';
import { supabase } from './supabase';
// import OnboardingWizard from './components/OnboardingWizard'; // PHASE 2
import SuccessPage from './components/SuccessPage';
// PHASE 2: import WeekendBookingWizard from './components/WeekendBookingWizard';
import WeekendSuccessPage from './components/WeekendSuccessPage';
import OneTimeBookingWizard from './components/OneTimeBookingWizard';
import OneTimeSuccessPage from './components/OneTimeSuccessPage';
import HomePage from './components/HomePage';
import InteriorMaintenancePage from './components/InteriorMaintenancePage';
import InteriorQuizPage from './components/InteriorQuizPage';
import MaintenanceBookingWizard from './components/MaintenanceBookingWizard';
import MaintenanceConfirmedPage from './components/MaintenanceConfirmedPage';
import { ONE_TIME_PACKAGES, RUSH_FEE, SHOP_PHONE_SMS } from './data/constants';
import { initMetaPixel, captureAttribution, trackPageView, trackEvent } from './utils/metaTracking';
import AdminLogin from './components/admin/AdminLogin';
import PrivateRoute from './components/admin/PrivateRoute';
// New admin system
import NewAdminLayout from './admin/AdminLayout';
import Dashboard from './admin/pages/Dashboard';
import Clients from './admin/pages/Clients';
import ClientProfile from './admin/pages/ClientProfile';
import Bookings from './admin/pages/Bookings';
import CalendarPage from './admin/pages/Calendar';
import Payments from './admin/pages/Payments';
import MaintenanceProgram from './admin/pages/MaintenanceProgram';
import JobDetail from './admin/pages/JobDetail';
import Settings from './admin/pages/Settings';
import Activities from './admin/pages/Activities';
import WinBack from './admin/pages/WinBack';
import AdminBookingWizard from './admin/pages/AdminBookingWizard';
import EducationPage from './components/EducationPage';
import SplashScreen from './components/SplashScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import InvoicePayPage from './components/InvoicePayPage';
import InvoiceThankYouPage from './components/InvoiceThankYouPage';
import PromoPage from './components/PromoPage';
import HalloweenPage from './components/HalloweenPage';
import FlyerPage from './components/FlyerPage';
import BookNowPage from './components/BookNowPage';
import RaffleEntryPage from './components/RaffleEntryPage';
import Raffle from './admin/pages/Raffle';
import Inventory from './admin/pages/Inventory';

// ==========================================
// 1. DATA CONSTANTS
// ==========================================
const carMakes = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 
  'Dodge', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 
  'Jeep', 'Kia', 'Lexus', 'Lincoln', 'Mazda', 'Mercedes-Benz', 'Nissan', 
  'Ram', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
];

const carModels = {
  'Acura': ['ILX', 'TLX', 'MDX', 'RDX', 'NSX'],
  'Audi': ['A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'Q3', 'Q5', 'Q7', 'Q8', 'e-tron'],
  'BMW': ['2 Series', '3 Series', '4 Series', '5 Series', '7 Series', 'X1', 'X3', 'X5', 'X7', 'i4', 'iX'],
  'Buick': ['Encore', 'Encore GX', 'Envision', 'Enclave'],
  'Cadillac': ['CT4', 'CT5', 'XT4', 'XT5', 'XT6', 'Escalade', 'LYRIQ'],
  'Chevrolet': ['Spark', 'Malibu', 'Camaro', 'Corvette', 'Trax', 'Equinox', 'Blazer', 'Traverse', 'Tahoe', 'Suburban', 'Silverado'],
  'Chrysler': ['300', 'Pacifica'],
  'Dodge': ['Charger', 'Challenger', 'Durango', 'Hornet'],
  'Ford': ['Fiesta', 'Focus', 'Fusion', 'Mustang', 'EcoSport', 'Escape', 'Edge', 'Explorer', 'Expedition', 'F-150', 'Bronco', 'Maverick'],
  'Genesis': ['G70', 'G80', 'G90', 'GV60', 'GV70', 'GV80'],
  'GMC': ['Terrain', 'Acadia', 'Yukon', 'Sierra'],
  'Honda': ['Civic', 'Accord', 'Insight', 'HR-V', 'CR-V', 'Passport', 'Pilot', 'Odyssey', 'Ridgeline'],
  'Hyundai': ['Accent', 'Elantra', 'Sonata', 'Venue', 'Kona', 'Tucson', 'Santa Fe', 'Palisade', 'Ioniq 5'],
  'Infiniti': ['Q50', 'Q60', 'QX50', 'QX55', 'QX60', 'QX80'],
  'Jeep': ['Renegade', 'Compass', 'Cherokee', 'Grand Cherokee', 'Wrangler', 'Gladiator', 'Wagoneer', 'Grand Wagoneer'],
  'Kia': ['Rio', 'Forte', 'K5', 'Stinger', 'Soul', 'Seltos', 'Sportage', 'Sorento', 'Telluride', 'EV6'],
  'Lexus': ['IS', 'ES', 'LS', 'UX', 'NX', 'RX', 'GX', 'LX', 'RZ'],
  'Lincoln': ['Corsair', 'Nautilus', 'Aviator', 'Navigator'],
  'Mazda': ['Mazda3', 'Mazda6', 'CX-30', 'CX-5', 'CX-50', 'CX-9', 'MX-5 Miata'],
  'Mercedes-Benz': ['A-Class', 'C-Class', 'E-Class', 'S-Class', 'GLA', 'GLB', 'GLC', 'GLE', 'GLS', 'EQS'],
  'Nissan': ['Versa', 'Sentra', 'Altima', 'Maxima', 'Kicks', 'Rogue', 'Murano', 'Pathfinder', 'Armada', 'Frontier', 'Titan', 'Ariya'],
  'Ram': ['1500', '2500', '3500'],
  'Subaru': ['Impreza', 'Legacy', 'Crosstrek', 'Outback', 'Forester', 'Ascent', 'Solterra'],
  'Tesla': ['Model 3', 'Model S', 'Model X', 'Model Y'],
  'Toyota': ['Corolla', 'Camry', 'Avalon', 'Prius', 'C-HR', 'RAV4', 'Highlander', 'Sequoia', '4Runner', 'Tacoma', 'Tundra', 'bZ4X'],
  'Volkswagen': ['Jetta', 'Passat', 'Arteon', 'Taos', 'Tiguan', 'Atlas', 'ID.4'],
  'Volvo': ['S60', 'S90', 'V60', 'V90', 'XC40', 'XC60', 'XC90', 'C40']
};

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
const currentYear = 2026;
const years = Array.from({ length: currentYear - 1999 }, (_, i) => currentYear - i);

// ==========================================
// INPUT SANITIZATION UTILITY
// ==========================================
const sanitizeInput = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/[<>]/g, '')      // Strip angle brackets (XSS prevention)
    .replace(/javascript:/gi, '') // Strip JS protocol
    .trim();
};

const sanitizeFormData = (data) => {
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    sanitized[key] = typeof value === 'string' ? sanitizeInput(value) : value;
  }
  return sanitized;
};

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// ==========================================
// EMAIL/SMS NOTIFICATION SERVICE
// ==========================================
const sendNotification = async (formData, paymentStatus) => {
  try {
    // This is a placeholder - you'll need to set up your backend endpoint
    const emailData = {
      to: 'your-email@example.com', // Replace with your email
      subject: '🦋 New Monarch Program Application',
      body: `
NEW APPLICATION RECEIVED

Customer Information:
- Name: ${formData.name}
- Phone: ${formData.phone}
- Email: ${formData.email}
- Home Address: ${formData.homeAddress}
- Work Address: ${formData.workAddress}

Vehicle Information:
- Make: ${formData.make}
- Model: ${formData.model}
- Year: ${formData.year}

Schedule Preference:
- Week: ${formData.preferredWeek}
- Day: ${formData.preferredDay}

Agreement:
- Accepted: ${formData.acceptedAgreement ? 'Yes' : 'No'}
- Signature: ${formData.signature}

Payment Status: ${paymentStatus}

---
Monarch Detailing - Automotive Alchemy
      `
    };

    // Option 1: Using EmailJS (Free service)
    // You'll need to sign up at https://www.emailjs.com/ and get your keys
    // emailjs.send('YOUR_SERVICE_ID', 'YOUR_TEMPLATE_ID', emailData, 'YOUR_PUBLIC_KEY');

    // Option 2: Using your own backend
    // await fetch('https://your-backend.com/api/send-notification', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(emailData)
    // });

    // Option 3: Using Formspree (easiest)
    await fetch('https://formspree.io/f/xgokjjly', { // Replace with your Formspree form ID
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(emailData)
    });

    console.log('Notification sent successfully!');
  } catch (error) {
    console.error('Error sending notification:', error);
  }
};



// ==========================================
// 2. PAGE COMPONENTS (Stable Structure)
// ==========================================

// ─── Spot counter badge (used across multiple pages) ───
// Color shifts: sky blue (plenty) → amber (≤10) → red pulsing (≤5)
const SpotsBadge = ({ spotsLeft, className = '' }) => {
  const urgent  = spotsLeft !== null && spotsLeft <= 5;
  const warning = spotsLeft !== null && spotsLeft <= 10 && spotsLeft > 5;
  const label   = spotsLeft === null
    ? 'Only 20 Exclusive Spots Available'
    : spotsLeft === 0
    ? 'Program Full — Join the Waitlist'
    : `Only ${spotsLeft} of 20 Spot${spotsLeft === 1 ? '' : 's'} Remaining`;

  const colorClass = urgent
    ? 'bg-red-500/10 border-red-400 text-red-400 shadow-[0_0_20px_rgba(239,68,68,0.3)]'
    : warning
    ? 'bg-amber-500/10 border-amber-400 text-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.3)]'
    : 'bg-[#68caf1]/10 border-[#68caf1] text-[#68caf1] shadow-[0_0_20px_rgba(104,202,241,0.3)]';

  return (
    <div className={`inline-flex items-center gap-2 border-2 px-10 py-3 rounded-full font-black uppercase tracking-widest text-sm ${colorClass} ${className}`}>
      {urgent && spotsLeft > 0 && (
        <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
      )}
      {label}
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const ProgramPage = ({
  currentStep,
  handleNextStep,
  handlePrevStep,
  formData,
  handleInputChange,
  setShowAgreement,
  validationErrors,
  paymentCompleted,
  setPaymentCompleted,
  setActivePage,
  setCurrentStep,
  isSubmitting,
  setIsSubmitting,
  spotsLeft,
}) => {

  // Validation function for each step
  const validateStep = (step) => {
    const errors = [];
    
    if (step === 2) {
      // Personal Information validation
      if (!formData.name.trim()) errors.push('Full name is required');
      if (!formData.phone.trim()) errors.push('Phone number is required');
      if (formData.phone.length !== 10) errors.push('Phone must be 10 digits');
      if (!formData.email.trim()) errors.push('Email is required');
      if (formData.email.trim() && !EMAIL_REGEX.test(formData.email.trim())) errors.push('Please enter a valid email address');
      if (!formData.homeAddress.trim()) errors.push('Home address is required');
      if (!formData.workAddress.trim()) errors.push('Work address is required');
      
      // Car Information validation
      if (!formData.make) errors.push('Car make is required');
      if (!formData.model) errors.push('Car model is required');
      if (!formData.year) errors.push('Car year is required');
    }
    
    if (step === 3) {
      // Schedule validation
      if (!formData.preferredWeek) errors.push('Preferred week is required');
      if (!formData.preferredDay) errors.push('Preferred day is required');
      
      // Agreement validation
      if (!formData.acceptedAgreement) errors.push('You must accept the Monarch Maintenance Agreement');
      if (!formData.signature.trim()) errors.push('Signature is required');
    }
    
    return errors;
  };

  // Handle Continue button with validation
  const handleContinue = () => {
    const errors = validateStep(currentStep);
    
    if (errors.length > 0) {
      alert('Please complete all required fields:\n\n' + errors.join('\n'));
      return;
    }
    
    handleNextStep();
    
    // Scroll to the next section after state updates
    setTimeout(() => {
      if (currentStep === 1) {
        document.getElementById('personal-info-header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (currentStep === 2) {
        document.getElementById('schedule-header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (currentStep === 3) {
        document.getElementById('payment-header')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Handle final submission with email/SMS notification
  const handleFinalSubmit = async () => {
    if (!paymentCompleted) {
      alert('Please complete the $50 deposit payment before submitting your application.');
      return;
    }

    // Prevent double submissions
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    try {
      // Sanitize all form data before saving
      const cleanData = sanitizeFormData(formData);
      
      // Enrollment is handled by Stripe webhook — just send Formspree notification
      await sendNotification(cleanData, 'Completed - $50 Deposit Paid').catch(err => console.warn('Formspree notification failed:', err));
      
      // Show success message
      alert(`✅ APPLICATION SUBMITTED SUCCESSFULLY!\n\n🦋 Welcome to the Monarch Family, ${cleanData.name}!\n\nYou will receive a confirmation email at ${cleanData.email} shortly.\n\nYour scheduled day: ${cleanData.preferredWeek} - ${cleanData.preferredDay}\n\nWe're excited to begin this transformation journey with you!`);
      
      // Reset form and go to results page
      setActivePage('results');
      setCurrentStep(1);
      
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('There was an error submitting your application. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative py-28 px-6 text-center overflow-hidden">
        {/* Subtle radial glow */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(104,202,241,0.08) 0%, transparent 70%)' }} />
        <div className="max-w-5xl mx-auto relative">
          <div className="flex justify-center mb-10">
            <img
              src="/images/logo.png"
              alt="Monarch Detailing Logo"
              className="h-36 md:h-48 w-auto object-contain drop-shadow-[0_0_24px_rgba(104,202,241,0.5)]"
            />
          </div>
          <div className="inline-block bg-[#68caf1]/10 border border-[#68caf1]/40 text-[#68caf1] px-5 py-1.5 rounded-full text-xs font-bold tracking-[0.25em] uppercase mb-6">
            Maintenance Program
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-5 tracking-tight text-white leading-none">
            Automotive Alchemy.<br />
            <span className="text-[#68caf1]">Monthly.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
            A membership program built for vehicle owners who refuse to settle. Premium monthly care — delivered to your door.
          </p>
          <SpotsBadge spotsLeft={spotsLeft} className="mb-0" />
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-[#0a0a0a] border-y border-gray-800 py-5 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            {[
              { n: 1, label: 'Overview' },
              { n: 2, label: 'Details' },
              { n: 3, label: 'Schedule' },
              { n: 4, label: 'Deposit' },
            ].map(({ n, label }, i, arr) => (
              <div key={n} className="flex items-center flex-1">
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    currentStep > n
                      ? 'bg-[#68caf1] text-black'
                      : currentStep === n
                      ? 'bg-[#68caf1] text-black ring-4 ring-[#68caf1]/20'
                      : 'bg-gray-800 text-gray-600'
                  }`}>
                    {currentStep > n ? <Check className="w-4 h-4" /> : n}
                  </div>
                  <span className={`text-[10px] font-medium hidden sm:block ${currentStep >= n ? 'text-[#68caf1]' : 'text-gray-600'}`}>
                    {label}
                  </span>
                </div>
                {i < arr.length - 1 && (
                  <div className={`flex-1 h-px mx-2 transition-colors ${currentStep > n ? 'bg-[#68caf1]' : 'bg-gray-800'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 1: Program Information */}
      {currentStep === 1 && (
        <section className="py-20 px-6 bg-black text-white">
          <div className="max-w-5xl mx-auto">

            {/* Section label */}
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#68caf1]/50" />
              <span className="text-[#68caf1] text-xs font-bold tracking-[0.3em] uppercase">The Program</span>
              <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#68caf1]/50" />
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 tracking-tight">
              Membership Starts With Transformation
            </h2>
            <p className="text-center text-gray-400 mb-16 max-w-2xl mx-auto text-lg leading-relaxed">
              The Monarch Maintenance Program is not a car wash subscription. It's a relationship — built on consistency, craftsmanship, and a standard most shops can't match.
            </p>

            {/* Two-phase overview */}
            <div className="grid md:grid-cols-2 gap-5 mb-16">
              {/* Phase 1 */}
              <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 hover:border-[#68caf1]/40 transition-all duration-300">
                <div className="inline-block bg-[#68caf1]/10 border border-[#68caf1]/30 rounded-full px-4 py-1 text-xs font-bold text-[#68caf1] tracking-wider uppercase mb-5">
                  Phase One
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Alchemist Deep Clean</h3>
                <p className="text-gray-400 leading-relaxed mb-6 text-sm">
                  Every member begins here. A comprehensive, multi-stage restoration that establishes the pristine baseline we'll maintain together — inside and out.
                </p>
                <div className="flex items-end gap-4 mb-2">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Sedan</p>
                    <p className="text-3xl font-bold text-white">$250</p>
                  </div>
                  <div className="text-gray-700 text-xl font-light mb-1">/</div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">SUV & Truck</p>
                    <p className="text-3xl font-bold text-white">$275</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600">Pricing may vary based on vehicle condition</p>
              </div>

              {/* Phase 2 */}
              <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 hover:border-[#68caf1]/40 transition-all duration-300">
                <div className="inline-block bg-[#68caf1]/10 border border-[#68caf1]/30 rounded-full px-4 py-1 text-xs font-bold text-[#68caf1] tracking-wider uppercase mb-5">
                  Phase Two
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Monthly Maintenance</h3>
                <p className="text-gray-400 leading-relaxed mb-6 text-sm">
                  Once your baseline is set, we lock in your dedicated service day. Same time, same standard — every single month at your home or workplace.
                </p>
                <div className="flex items-end gap-4 mb-2">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Sedan</p>
                    <p className="text-3xl font-bold text-[#68caf1]">$125</p>
                  </div>
                  <div className="text-gray-700 text-xl font-light mb-1">/</div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">SUV & Truck</p>
                    <p className="text-3xl font-bold text-[#68caf1]">$150</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600">Per month · billed after service</p>
              </div>
            </div>

            {/* What membership includes */}
            <div className="mb-16">
              <h3 className="text-sm font-bold text-[#68caf1] tracking-[0.3em] uppercase text-center mb-8">What Membership Includes</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { title: 'Dedicated Service Day', desc: 'Your assigned weekday, 9:00 AM — no scheduling, no chasing. We show up.' },
                  { title: 'Mobile Service', desc: 'We come to your home or workplace in St. Mary\'s, Charles, or Calvert County.' },
                  { title: 'Member Perks', desc: 'Monthly gifts, premium car care products, and exclusive member treats.' },
                  { title: 'Relationship-Driven', desc: 'We learn your vehicle. We communicate proactively. We hold ourselves accountable.' },
                  { title: 'Transparent Pricing', desc: 'Fixed monthly rates. No surprise fees. You know exactly what you\'re paying.' },
                  { title: 'Consistent Standard', desc: 'Every visit delivers the same meticulous, multi-step process — no shortcuts.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 bg-[#111111] border border-gray-800 rounded-xl">
                    <div className="w-5 h-5 rounded-full bg-[#68caf1]/10 border border-[#68caf1]/30 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-[#68caf1]" />
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm">{item.title}</p>
                      <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Service area + schedule note */}
            <div className="bg-[#68caf1]/5 border border-[#68caf1]/20 rounded-2xl p-6 mb-16 text-center">
              <p className="text-gray-300 text-sm leading-relaxed">
                <span className="text-[#68caf1] font-semibold">Scheduling note:</span> Alchemist Deep Cleans begin{' '}
                <span className="text-white font-semibold">April 1st, 2026</span> · Monthly Maintenance begins{' '}
                <span className="text-white font-semibold">June 1st, 2026</span>
              </p>
              <p className="text-gray-500 text-xs mt-2">Serving St. Mary's · Charles · Calvert County</p>
            </div>

            {/* Continue */}
            <div className="text-center">
              <button
                onClick={handleContinue}
                className="bg-[#68caf1] text-black px-12 py-4 rounded-xl font-bold text-lg hover:bg-[#5bb8db] transition-all inline-flex items-center gap-3"
              >
                Apply for Membership
                <ChevronRight className="w-5 h-5" />
              </button>
              <p className="text-gray-600 text-xs mt-3">{spotsLeft} spots remaining · Deposit: $50</p>
            </div>
          </div>
        </section>
      )}

      {/* Step 2: Personal & Vehicle Information */}
      {currentStep === 2 && (
        <section className="py-16 px-6 bg-black text-white">
          <div className="max-w-4xl mx-auto">
            <h2 id="personal-info-header" className="text-4xl md:text-5xl font-bold text-white mb-4 text-center tracking-tight">
              Your Information
            </h2>
            <p className="text-center text-gray-500 mb-12">Contact and vehicle details for your membership</p>

            {/* Personal Information */}
            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <User className="w-6 h-6 text-[#68caf1]" />
                <h3 className="text-xl font-bold text-white">Personal Information</h3>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-400 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                      placeholder="1234567890"
                      maxLength="10"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">10 digits, numbers only</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-400 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Home Address *</label>
                  <input
                    type="text"
                    name="homeAddress"
                    value={formData.homeAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    placeholder="123 Main St, City, State ZIP"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Your home address in St. Mary's, Charles, or Calvert County</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Work Address *</label>
                  <input
                    type="text"
                    name="workAddress"
                    value={formData.workAddress}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    placeholder="456 Business Blvd, City, State ZIP"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Only applicable if your work address is in St. Mary's, Charles, or Calvert County</p>
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <Car className="w-6 h-6 text-[#68caf1]" />
                <h3 className="text-xl font-bold text-white">Vehicle Information</h3>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Make *</label>
                  <select
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    required
                  >
                    <option value="">Select Make</option>
                    {carMakes.map(make => (
                      <option key={make} value={make}>{make}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Model *</label>
                  <select
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    required
                    disabled={!formData.make}
                  >
                    <option value="">Select Model</option>
                    {formData.make && carModels[formData.make]?.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Year *</label>
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    required
                  >
                    <option value="">Select Year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevStep}
                className="px-8 py-3 border border-gray-700 text-gray-400 rounded-lg font-bold hover:border-gray-500 transition-all"
              >
                ← Back
              </button>
              <button
                onClick={handleContinue}
                className="bg-[#68caf1] text-black px-12 py-4 rounded-xl font-bold text-lg hover:bg-[#5bb8db] transition-all"
              >
                Continue →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Step 3: Schedule & Agreement */}
      {currentStep === 3 && (
        <section className="py-16 px-6 bg-black text-white">
          <div className="max-w-4xl mx-auto">
            <h2 id="schedule-header" className="text-4xl md:text-5xl font-bold text-white mb-4 text-center tracking-tight">
              Schedule & Agreement
            </h2>
            <p className="text-center text-gray-500 mb-12">Choose your preferred schedule and review the agreement</p>

            {/* Scheduling */}
            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-[#68caf1]" />
                <h3 className="text-xl font-bold text-white">Your Monthly Schedule</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Preferred Week *</label>
                  <select
                    name="preferredWeek"
                    value={formData.preferredWeek}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    required
                  >
                    <option value="">Select Week</option>
                    {weeks.map(week => (
                      <option key={week} value={week}>{week}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Preferred Day *</label>
                  <select
                    name="preferredDay"
                    value={formData.preferredDay}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none text-white placeholder-gray-600"
                    required
                  >
                    <option value="">Select Day</option>
                    {weekdays.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="bg-[#68caf1]/5 border border-[#68caf1]/20 rounded-xl p-4">
                <p className="text-sm text-[#68caf1]/80">
                  Your preferred schedule will be confirmed based on availability. We'll contact you within 48 hours to finalize your standing appointment.
                </p>
              </div>
            </div>

            {/* Agreement */}
            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="w-6 h-6 text-[#68caf1]" />
                <h3 className="text-xl font-bold text-white">Monarch Maintenance Agreement</h3>
              </div>

              <div className="bg-black/60 rounded-xl p-6 mb-6 max-h-80 overflow-y-auto border border-gray-800">
                <h4 className="font-bold text-sm text-[#68caf1] tracking-wider uppercase mb-4">Terms and Conditions</h4>
                
                <div className="space-y-4 text-gray-400 text-sm">
                  <p className="font-semibold">1. Program Commitment</p>
                  <p>By enrolling in the Monarch Maintenance Program, you agree to a minimum 6-month commitment. This commitment ensures consistent care and allows us to maintain your vehicle at peak condition.</p>

                  <p className="font-semibold">2. Initial Deep Clean Requirement</p>
                  <p>All vehicles must undergo the Alchemist Deep Clean before beginning monthly maintenance. This establishes the baseline condition we will maintain throughout your membership.</p>

                  <p className="font-semibold">3. Monthly Maintenance Schedule</p>
                  <p>You will receive a dedicated weekly time slot for your monthly maintenance detail. This is your standing appointment and should be treated with the same importance as any professional commitment.</p>

                  <p className="font-semibold">4. Payment Terms</p>
                  <p>Monthly maintenance fees are due at the beginning of each month. The initial $50 deposit will be applied to your Alchemist Deep Clean service.</p>

                  <p className="font-semibold">5. Cancellation Policy</p>
                  <p>Appointments must be rescheduled with at least 48 hours notice. Last-minute cancellations may result in forfeiture of that month's service without refund.</p>

                  <p className="font-semibold">6. Vehicle Accessibility</p>
                  <p>You agree to provide reasonable access to your vehicle at the scheduled time, whether at your home or work address. Keys or access instructions must be provided in advance when necessary.</p>

                  <p className="font-semibold">7. Termination</p>
                  <p>After the initial 6-month commitment, either party may terminate this agreement with 30 days written notice. Early termination within the first 6 months requires payment of remaining monthly fees.</p>

                  <p className="font-semibold">8. Quality Guarantee</p>
                  <p>We stand behind our work. If you're not satisfied with any service, notify us within 48 hours and we will make it right.</p>

                  <p className="font-semibold">9. Communication</p>
                  <p>You agree to maintain open communication regarding schedule changes, service concerns, or special requests. We expect timely responses to our communications as well.</p>

                  <p className="font-semibold">10. Respect and Professionalism</p>
                  <p>This is a partnership built on mutual respect. We expect courteous treatment of our team members and their expertise.</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="acceptedAgreement"
                    checked={formData.acceptedAgreement}
                    onChange={handleInputChange}
                    className="mt-1 w-5 h-5 accent-[#68caf1] rounded focus:ring-[#68caf1]"
                    required
                  />
                  <span className="text-sm text-gray-400">
                    I have read and agree to the terms and conditions of the Monarch Maintenance Agreement *
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Digital Signature *</label>
                <input
                  type="text"
                  name="signature"
                  value={formData.signature}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-[#0d0d0d] border border-gray-700 rounded-lg focus:border-[#68caf1] focus:outline-none font-signature text-2xl text-white placeholder-gray-700"
                  placeholder="Type your full name"
                  required
                />
                <p className="text-xs text-gray-600 mt-1">This constitutes your legal signature</p>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevStep}
                className="px-8 py-3 border border-gray-700 text-gray-400 rounded-lg font-bold hover:border-gray-500 transition-all"
              >
                ← Back
              </button>
              <button
                onClick={handleContinue}
                className="bg-[#68caf1] text-black px-12 py-4 rounded-xl font-bold text-lg hover:bg-[#5bb8db] transition-all"
              >
                Continue to Payment →
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Step 4: Deposit Payment */}
      {currentStep === 4 && (
        <section className="py-16 px-6 bg-black text-white">
          <div className="max-w-4xl mx-auto">
            <h2 id="payment-header" className="text-4xl md:text-5xl font-bold text-white mb-4 text-center tracking-tight">
              Secure Your Spot
            </h2>
            <p className="text-center text-gray-500 mb-12">Complete your $50 deposit to reserve your Monarch membership</p>

            {/* Payment Info */}
            <div className="bg-[#111111] border border-gray-800 rounded-2xl p-8 mb-8">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="w-6 h-6 text-[#68caf1]" />
                <h3 className="text-xl font-bold text-white">Deposit Payment</h3>
              </div>

              <div className="bg-[#68caf1]/5 border border-[#68caf1]/20 rounded-xl p-6 mb-8">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-semibold text-gray-400">Deposit Amount</span>
                  <span className="text-4xl font-bold text-[#68caf1]">$50.00</span>
                </div>
                <div className="border-t border-gray-800 pt-4 space-y-1">
                  {[
                    'Secures one of only 20 available membership spots',
                    'Applied directly to your Alchemist Deep Clean',
                    'Fully refundable if we cannot accommodate your schedule',
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-400">
                      <Check className="w-4 h-4 text-[#68caf1] shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Application Summary */}
              <div className="bg-black/40 border border-gray-800 rounded-xl p-5 mb-8">
                <h4 className="text-xs font-bold text-[#68caf1] tracking-wider uppercase mb-4">Application Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name</span>
                    <span className="font-semibold text-white">{formData.name || 'Not provided'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span className="font-semibold text-white">{formData.email || 'Not provided'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Vehicle</span>
                    <span className="font-semibold text-white">
                      {formData.year} {formData.make} {formData.model}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Schedule</span>
                    <span className="font-semibold text-white">
                      {formData.preferredWeek}, {formData.preferredDay}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stripe Payment Button */}
              <div className="space-y-4">
                {!paymentCompleted ? (
                  <>
                    <a
                      href="https://buy.stripe.com/test_dRm00cbvcgfnam5eZu9Zm00"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        // ⚠️ SECURITY TODO: Replace this client-side timeout with 
                        // server-side Stripe webhook verification. Currently this 
                        // auto-confirms after 2s without verifying actual payment.
                        // See: https://stripe.com/docs/webhooks
                        setTimeout(() => {
                          setPaymentCompleted(true);
                        }, 2000);
                      }}
                      className="w-full bg-[#635bff] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#5048e5] transition-all shadow-lg flex items-center justify-center gap-3"
                    >
                      <CreditCard className="w-6 h-6" />
                      Pay $50 Deposit with Stripe
                    </a>
                    <p className="text-xs text-center text-gray-500">
                      You'll be redirected to Stripe's secure payment page. Replace this link with your actual Stripe payment link.
                    </p>
                  </>
                ) : (
                  <div className="bg-emerald-900/20 border border-emerald-600 rounded-xl p-6 text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <div className="bg-emerald-500 rounded-full p-1.5">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="text-lg font-bold text-emerald-400">Payment Received</h4>
                    </div>
                    <p className="text-emerald-300/70 text-sm">
                      Your deposit has been processed. Submit your application below.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevStep}
                className="px-8 py-3 border border-gray-700 text-gray-400 rounded-lg font-bold hover:border-gray-500 transition-all"
              >
                ← Back
              </button>
              <button
                onClick={handleFinalSubmit}
                disabled={!paymentCompleted || isSubmitting}
                className={`px-12 py-4 rounded-xl font-bold text-lg transition-all ${
                  paymentCompleted && !isSubmitting
                    ? 'bg-emerald-500 text-white hover:bg-emerald-400'
                    : 'bg-gray-800 text-gray-600 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? 'Submitting…' : paymentCompleted ? 'Submit Application' : 'Complete Payment First'}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// ==========================================
// 3. FOUNDATIONS PAGE - YOUR PERSONAL STORY
// ==========================================

const ContactStrip = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <a href="tel:+13018859767" className="bg-white border border-gray-200 shadow-sm hover:border-[#68caf1]/50 rounded-2xl p-6 flex items-center gap-4 transition-all group">
      <div className="w-12 h-12 rounded-full bg-[#68caf1]/10 flex items-center justify-center group-hover:bg-[#68caf1]/20 transition-all shrink-0">
        <Phone className="w-5 h-5 text-[#68caf1]" />
      </div>
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-0.5">Call or Text</p>
        <p className="text-[#111827] font-bold">(301) 885-9767</p>
      </div>
    </a>
    <a href="mailto:info.monarchdetailing@gmail.com" className="bg-white border border-gray-200 shadow-sm hover:border-[#68caf1]/50 rounded-2xl p-6 flex items-center gap-4 transition-all group">
      <div className="w-12 h-12 rounded-full bg-[#68caf1]/10 flex items-center justify-center group-hover:bg-[#68caf1]/20 transition-all shrink-0">
        <Mail className="w-5 h-5 text-[#68caf1]" />
      </div>
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-0.5">Email Us</p>
        <p className="text-[#111827] font-bold text-sm break-all">info.monarchdetailing@gmail.com</p>
      </div>
    </a>
    <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-6 flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-[#68caf1]/10 flex items-center justify-center shrink-0">
        <Clock className="w-5 h-5 text-[#68caf1]" />
      </div>
      <div>
        <p className="text-xs text-gray-500 uppercase tracking-widest mb-0.5">Availability</p>
        <p className="text-[#111827] font-bold">By Appointment Only</p>
      </div>
    </div>
  </div>
);

const FoundationsPage = () => (
  <div className="min-h-screen text-[#111827]">

    {/* Hero photo */}
    <section className="relative h-[50vh] overflow-hidden">
      <img
        src="/images/b3db6067-f9bc-4512-a695-cd8d73193ff3.jpg"
        alt="Detailing in progress"
        className="w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-[#EAF0F6]" />
      <div className="absolute bottom-12 left-0 right-0 text-center px-6">
        <p className="text-[#68caf1] text-sm font-bold uppercase tracking-[0.2em] mb-3">About Monarch</p>
        <h1 className="text-5xl md:text-6xl font-bold leading-tight">
          The Story Behind<br /><span className="text-[#68caf1]">The Alchemy</span>
        </h1>
      </div>
    </section>

    {/* Contact — top */}
    <section className="px-6 py-10 bg-transparent">
      <div className="max-w-4xl mx-auto">
        <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-5 text-center">Get In Touch</p>
        <ContactStrip />
      </div>
    </section>

    <section className="py-20 px-6">
      <div className="max-w-4xl mx-auto">

        {/* Personal story */}
        <div className="mb-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Who I Am</p>
            <h2 className="text-3xl font-bold text-[#111827] mb-6">Not a car wash.<br />A calling.</h2>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              My name is Akeem James. A great detail is never just one thing — it's the product of patience, experience, and genuine passion. The kind of passion that makes you treat every client's vehicle like it's your own, because you understand what's at stake. When I work on your car, I'm thinking about everyone who rides in it: the kids in the back seat, the family coming to visit, the friend who notices the shine. That ripple matters to me.
            </p>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              Cleanliness, smiles, and compassion are contagious. Every vehicle I touch is an opportunity to send something good back into someone's day — and into the lives of everyone they carry. I don't just detail cars. I care about the people inside them, and I want every client to feel that.
            </p>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              In 2017, I lost my sister <span className="text-[#111827] font-semibold">Akeema James</span> in a car accident. Monarch Detailing is dedicated to her memory. Her name lives on my license plate, in this business, and in every car I touch. A clean car isn't just about appearance — it's about safety, presence, and love.
            </p>
            <p className="text-[#6B7280] leading-relaxed">
              Keeping vehicles clean and safe, and bringing love to this community one detail at a time — that's my way of carrying her forward.
            </p>
          </div>
          <div className="relative">
            <img
              src="/images/5975162c-f25b-435c-bcda-ef7227fbd3ae.jpg"
              alt="Professional detailing work"
              className="rounded-2xl w-full h-80 object-cover"
            />
            <div className="absolute -bottom-4 -left-4 bg-[#68caf1] text-black rounded-xl px-5 py-3 font-bold text-sm shadow-lg">
              Founded in Southern Maryland
            </div>
          </div>
        </div>

        {/* Why Monarch */}
        <div className="mb-20">
          <div className="border-l-4 border-[#68caf1] pl-8 mb-12">
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-3">The Name</p>
            <h2 className="text-3xl font-bold text-[#111827] mb-4">Why "Monarch"?</h2>
            <p className="text-[#6B7280] leading-relaxed text-lg">
              The monarch butterfly doesn't just change — it completely transforms. What goes in and what comes out are two entirely different things. That's the standard I hold myself to with every vehicle. Not a cleanup. A transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="relative rounded-2xl overflow-hidden h-48">
              <img src="/images/beforeafter/after_interior.jpg" alt="Interior detail" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <p className="absolute bottom-4 left-4 font-bold text-sm">Interior Work</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-48">
              <img src="/images/6941848e-7e93-4501-abc6-064d783413d1.jpg" alt="Clay and seal treatment" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <p className="absolute bottom-4 left-4 font-bold text-sm">Clay & Seal</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-48">
              <img src="/images/exterior-shot.jpg" alt="Exterior detail" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <p className="absolute bottom-4 left-4 font-bold text-sm">Exterior Protection</p>
            </div>
          </div>
        </div>

        {/* Products */}
        <div className="mb-20 bg-white border border-gray-200 shadow-sm rounded-2xl p-8 md:p-12">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">The Standard</p>
          <h2 className="text-3xl font-bold text-[#111827] mb-4">Professional-Grade Products Only</h2>
          <p className="text-[#6B7280] leading-relaxed mb-10">
            I don't use what you find at an auto parts store. Every product in my kit is chosen because it's what professional shops and concours competitors trust. The chemistry matters — the wrong product on the wrong surface causes damage that's invisible until it's permanent.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: 'Koch Chemie',
                origin: 'Germany',
                desc: 'pH-neutral formulas engineered for aggressive cleaning without stripping sealants or damaging clear coat. The benchmark for European-standard paint chemistry.',
              },
              {
                name: 'P&S 3-Step System',
                origin: 'Professional Grade',
                desc: 'A disciplined decontaminate → correct → protect protocol. Every step is intentional: remove embedded contaminants, restore clarity, then lock in protection.',
              },
              {
                name: 'Professional Blends',
                origin: 'Task-Specific',
                desc: 'Additional products selected for specific surfaces and conditions — leather, fabric, glass, rubber, trim. Right tool, right material, every time.',
              },
            ].map(({ name, origin, desc }) => (
              <div key={name} className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-1">{origin}</p>
                <h3 className="text-xl font-bold text-[#111827] mb-3">{name}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Service area + perks */}
        <div className="mb-20 grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-5">
              <MapPin className="w-6 h-6 text-[#68caf1]" />
              <h3 className="text-xl font-bold text-[#111827]">Where I Work</h3>
            </div>
            <p className="text-[#6B7280] text-sm mb-5">Fully mobile — I come to your home or workplace. You don't move your car. I move my kit.</p>
            <ul className="space-y-3">
              {["St. Mary's County", "Charles County", "Calvert County"].map(c => (
                <li key={c} className="flex items-center gap-3 font-semibold text-[#111827]">
                  <ChevronRight className="w-5 h-5 text-[#68caf1]" />{c}
                </li>
              ))}
            </ul>
            <p className="text-gray-500 text-xs mt-5">By Appointment Only</p>
          </div>
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-5">
              <Gift className="w-6 h-6 text-[#68caf1]" />
              <h3 className="text-xl font-bold text-[#111827]">Member Perks</h3>
            </div>
            <p className="text-[#6B7280] text-sm mb-5">Program members get more than a clean car every month.</p>
            <ul className="space-y-3">
              {['Monthly gifts and car care treats', 'Premium product samples', 'Priority scheduling', 'Personalized maintenance advice'].map(p => (
                <li key={p} className="flex items-start gap-3 text-[#374151] text-sm">
                  <Check className="w-5 h-5 text-[#68caf1] flex-shrink-0 mt-0.5" />{p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact — bottom */}
        <div className="mb-20">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Get In Touch</p>
          <h2 className="text-3xl font-bold text-[#111827] mb-8">Contact Monarch Detailing</h2>
          <ContactStrip />
        </div>

        {/* CTA */}
        <div className="text-center py-12 border-t border-gray-200">
          <p className="text-[#6B7280] mb-6 text-lg">Ready to see what your car is actually capable of?</p>
          <Link to="/detail" className="bg-[#68caf1] text-black px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#5bb8db] transition-all inline-flex items-center gap-3">
            Book a Service <ChevronRight className="w-5 h-5" />
          </Link>
        </div>

      </div>
    </section>
  </div>
);

// ==========================================
// BEFORE/AFTER SLIDER COMPONENT
// ==========================================

const BeforeAfterSlider = ({ beforeImage, afterImage, label }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-12">
      <h3 className="text-xl font-bold text-center mb-4 text-[#68caf1]">{label}</h3>
      <div
        ref={containerRef}
        className="relative w-full aspect-[16/10] rounded-xl overflow-hidden border-2 border-[#68caf1] cursor-col-resize select-none"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {/* After Image (full width, sits behind) */}
        <div className="absolute inset-0">
          <img src={afterImage} alt="After" className="w-full h-full object-cover" />
          <div className="absolute bottom-3 right-3 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
            After
          </div>
        </div>

        {/* Before Image (clipped by slider position) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${sliderPosition}%` }}
        >
          <img
            src={beforeImage}
            alt="Before"
            className="h-full object-cover"
            style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%' }}
          />
          <div className="absolute bottom-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
            Before
          </div>
        </div>

        {/* Slider Line & Handle */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)]"
          style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
        >
          {/* Drag Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-[#68caf1]">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M6 10L2 10M2 10L5 7M2 10L5 13" stroke="#68caf1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 10L18 10M18 10L15 7M18 10L15 13" stroke="#68caf1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Instruction overlay (fades after interaction) */}
        {sliderPosition === 50 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/60 backdrop-blur-sm text-white text-sm font-semibold px-4 py-2 rounded-full">
              ← Drag to Compare →
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const beforeAfterData = [
  {
    label: 'Transformation #1 — Exterior Polish & Seal',
    before: '/images/before-exterior.jpg',
    after: '/images/after-exteior.jpg'
  },
  {
    label: 'Transformation #2 — Interior Deep Clean',
    before: '/images/beforeafter/before_interior.jpg',
    after: '/images/beforeafter/after_interior.jpg'
  },
];

// ==========================================
// 4. GALLERY/RESULTS PAGE
// ==========================================

const GalleryPage = ({ setActivePage, setCurrentStep, spotsLeft }) => (
  <div className="min-h-screen text-[#111827] relative overflow-hidden">
    {/* Hero photo */}
    <section className="relative h-[50vh] overflow-hidden">
      <img
        src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=1600&q=80"
        alt="Transformation results"
        className="w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-[#EAF0F6]" />
      <div className="absolute bottom-12 left-0 right-0 text-center px-6">
        <p className="text-[#68caf1] text-sm font-bold uppercase tracking-[0.2em] mb-3">Before &amp; After</p>
        <h1 className="text-5xl md:text-6xl font-bold leading-tight">
          Transformation<br /><span className="text-[#68caf1]">Gallery</span>
        </h1>
      </div>
    </section>

    <section className="py-20 px-6 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="shimmer-line max-w-sm mx-auto mb-16" />

        {/* Photo mosaic */}
        <div className="mb-20">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest text-center mb-8">The Work</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { src: '/images/raffle-interior-front-seat.jpg', label: 'Front Seat Detail' },
              { src: '/images/raffle-interior-console.jpg', label: 'Dash & Console' },
              { src: '/images/raffle-interior-floorboards.jpg', label: 'Spotless Floorboards' },
              { src: '/images/raffle-interior-rear-seat.jpg', label: 'Rear Seat Detail' },
              { src: '/images/raffle-interior-mat-keys.jpg', label: 'Ready to Go' },
              { src: '/images/raffle-interior-trunk.jpg', label: 'Trunk & Cargo' },
            ].map(({ src, label }) => (
              <div key={src} className="relative rounded-xl overflow-hidden aspect-square group">
                <img
                  src={src}
                  alt={label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <p className="absolute bottom-3 left-3 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Before/After Sliders */}
        <div className="max-w-3xl mx-auto mb-20">
          {beforeAfterData.map((item, index) => (
            <div key={index} className="reveal" style={{ transitionDelay: `${index * 150}ms` }}>
              <BeforeAfterSlider
                beforeImage={item.before}
                afterImage={item.after}
                label={item.sample ? `${item.label} ✦ Sample` : item.label}
              />
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mb-20">
          <h2 className="reveal text-4xl font-bold mb-12 text-center text-[#111827]">What Our Clients Say</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { text: "Akeem transformed my car from a daily driver into something that looks showroom fresh. The attention to detail is incredible!", name: "Sarah M., St. Mary's County" },
              { text: "The Monarch Maintenance Program is worth every penny. My SUV has never looked better, and the monthly perks are amazing!", name: "Michael T., Charles County" },
              { text: "I've tried other detailers, but none compare to Akeem's work. He truly treats your car like it's his own.", name: "Jennifer L., Calvert County" },
              { text: "The convenience of having Akeem come to my workplace is a game-changer. Professional service with exceptional results!", name: "David K., St. Mary's County" },
            ].map((review, i) => (
              <div key={i} className="reveal card-hover bg-white border border-gray-200 shadow-sm rounded-2xl p-8 hover:border-[#68caf1] transition-all duration-300" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-[#68caf1] text-[#68caf1]" />
                  ))}
                </div>
                <p className="text-lg text-[#6B7280] mb-4 italic">"{review.text}"</p>
                <p className="text-[#68caf1] font-semibold">— {review.name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-[#68caf1]/20 to-white border-2 border-[#68caf1] rounded-2xl p-12 text-center">
          <h2 className="text-4xl font-bold text-[#111827] mb-6">Ready to Transform Your Interior?</h2>
          <p className="text-xl text-[#6B7280] mb-6 max-w-2xl mx-auto">
            Book an interior detail today — we come to you anywhere in Southern Maryland.
          </p>
          <Link
            to="/detail"
            className="bg-[#68caf1] text-black px-12 py-4 rounded-lg font-bold text-lg hover:bg-[#5bb8db] transition-all shadow-lg inline-flex items-center gap-3"
          >
            Book Now
            <ChevronRight className="w-6 h-6" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

// ==========================================
// 5. ALCHEMIST DEEP CLEAN - PROCESS PAGE
// ==========================================

const AlchemistProcessPage = ({ setActivePage, setCurrentStep, spotsLeft }) => {

  const interiorSteps = [
    {
      number: '01',
      title: 'Blowout & Vacuum',
      subtitle: 'Clearing the Canvas',
      description: 'The interior transformation begins with a high-powered air blowout to dislodge dirt, dust, crumbs, and debris from every crack, crevice, and hard-to-reach area. This is immediately followed by a thorough vacuum of all surfaces — pulling up everything the blowout loosened and leaving no corner untouched.',
      areas: [
        'Seats (fabric or leather)',
        'Floor mats and carpet',
        'Center console and cup holders',
        'Door panels and pockets',
        'Cracks and crevices between seats',
        'Under seats and seat rails',
        'Trunk / cargo area',
        'Air vents and dashboard gaps',
      ],
    },
    {
      number: '02',
      title: 'Steam Clean & Wipe Down',
      subtitle: 'Deep Sanitization',
      description: 'Every surface is treated with a professional-grade commercial steamer — penetrating seams, crevices, and textured surfaces to kill bacteria, break down grease, and sanitize without harsh chemicals. Following the steam, each panel is hand-wiped with premium interior cleaners to lift residual grime and leave a clean, residue-free finish.',
      areas: [
        'Dashboard and instrument panel — full steam treatment',
        'Steering wheel, column, and controls',
        'Center console, cup holders, and armrests',
        'Door panels, handles, and jambs',
        'Seat surfaces and seatbacks — fabric and leather',
        'Seat belt buckles and webbing',
        'Gear shifter and surrounding trim',
        'Rearview mirror and sun visors',
      ],
    },
    {
      number: '03',
      title: 'Shampoo & Extraction',
      subtitle: 'Lifting What\'s Embedded',
      description: 'For fabrics that need a deeper reset, we apply a professional upholstery shampoo that breaks down embedded dirt, oils, pet dander, and stains at the fiber level. A commercial hot water extractor then pulls the loosened contamination — and the shampoo — completely out of the material. This step is performed as needed based on the condition of your interior.',
      areas: [
        'Carpet — full floor shampoo and hot water extraction',
        'Fabric seats — deep shampoo and extraction cycle',
        'Floor mats — shampooed and extracted (if applicable)',
        'Trunk / cargo area carpet (if applicable)',
        'Stain treatment — targeted pre-spray on heavy spots',
        'Odor neutralization — extraction removes odor at the source',
      ],
    },
    {
      number: '04',
      title: 'Final Touches',
      subtitle: 'The Details That Matter',
      description: 'The finishing stage is where the transformation becomes undeniable. Mirrors and all glass surfaces are cleaned to a streak-free shine. Plastic trim receives a UV-protective dressing to restore its deep finish. Leather seats are conditioned to maintain softness and prevent cracking — or fabric seats are left fresh and residue-free. A final quality check confirms every surface meets the Monarch standard before we close the door.',
      areas: [
        'Interior and exterior glass — streak-free finish',
        'Mirrors — cleaned and polished',
        'Plastic trim dressing — UV protection and restored appearance',
        'Leather conditioning — hydration and crack prevention',
        'Air freshener placement',
        'Final quality walk-through on all surfaces',
      ],
    },
  ];

  // PHASE 2: exterior process steps (paint decontamination, wash, clay, etc.)
  // Will be added back when exterior detailing launches.

  return (
    <div className="min-h-screen text-[#111827]">
      {/* Hero photo */}
      <section className="relative h-[50vh] overflow-hidden">
        <img
          src="/images/process-hero.jpg"
          alt="Detailing process"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-[#EAF0F6]" />
        <div className="absolute bottom-12 left-0 right-0 text-center px-6">
          <p className="text-[#68caf1] text-sm font-bold uppercase tracking-[0.2em] mb-3">Our Signature Process</p>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            The Interior<br /><span className="text-[#68caf1]">Detail Process</span>
          </h1>
        </div>
      </section>

      {/* Subtitle strip */}
      <section className="relative py-10 px-6 text-center">
        <div className="max-w-5xl mx-auto">
          <p className="text-xl md:text-2xl text-[#6B7280] max-w-3xl mx-auto mb-8 leading-relaxed">
            A meticulous 4-stage process focused entirely on what matters most — the space you live in every day. Every step is intentional. Every product is purposeful.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-[#6B7280]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#68caf1]"></div>
              <span>4-Stage Interior Process</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#68caf1]"></div>
              <span>Professional Steam Cleaning</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-[#68caf1]"></div>
              <span>Full Sanitization & Conditioning</span>
            </div>
          </div>
        </div>
      </section>

      {/* ========== PHASE 1: INTERIOR ========== */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#68caf1]/50"></div>
            <span className="text-[#68caf1] text-sm font-bold tracking-[0.3em] uppercase">Phase One</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#68caf1]/50"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-center text-[#111827] mb-4">Interior Restoration</h2>
          <p className="text-center text-[#6B7280] mb-16 max-w-2xl mx-auto">
            A 4-step process that clears, steam cleans, extracts, and conditions every surface your hands, eyes, and body touch — from the driver's seat to the trunk.
          </p>

          <div className="space-y-6">
            {interiorSteps.map((step, index) => (
              <div key={index} className="bg-white border border-gray-200 shadow-sm rounded-2xl overflow-hidden hover:border-[#68caf1]/40 transition-all duration-300">
                <div className="p-8 md:p-10">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 rounded-xl bg-[#68caf1]/10 border border-[#68caf1]/30 flex items-center justify-center">
                        <span className="text-[#68caf1] font-bold text-lg">{step.number}</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-[#68caf1] text-sm font-semibold tracking-wider uppercase mb-1">{step.subtitle}</div>
                      <h3 className="text-2xl md:text-3xl font-bold text-[#111827] mb-4">{step.title}</h3>
                      <p className="text-[#6B7280] leading-relaxed mb-6">{step.description}</p>
                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <h4 className="text-sm font-bold text-[#68caf1] tracking-wider uppercase mb-4">
                          {index === 2 ? 'Services Included' : 'Areas Addressed'}
                        </h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {step.areas.map((area, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-[#68caf1] flex-shrink-0 mt-0.5" />
                              <span className="text-[#6B7280] text-sm">{area}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA — interiors-only launch */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#68caf1]/10 to-white border-2 border-[#68caf1]/30 rounded-2xl p-10 md:p-14 text-center">
            <span className="text-5xl mb-6 block">✨</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6">Ready for Your Interior Transformation?</h2>
            <p className="text-[#6B7280] text-lg leading-relaxed max-w-2xl mx-auto mb-4">
              This is the full process behind The Alchemist, our top interior package. 4 stages, professional equipment, no shortcuts. Interior packages start at $75 for sedans.
            </p>
            <p className="text-[#6B7280] text-sm italic mb-10">
              Exterior detailing — paint decontamination, clay treatment, wax & sealant — coming soon as we expand our services.
            </p>
            <Link
              to="/detail"
              className="bg-[#68caf1] text-black px-12 py-4 rounded-lg font-bold text-lg hover:bg-[#5bb8db] transition-all shadow-lg inline-flex items-center gap-3"
            >
              Choose Your Package
              <ChevronRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==========================================
// 6. AGREEMENT MODAL
// ==========================================

const AgreementModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#68caf1] px-8 py-6 flex items-center justify-between">
          <h2 className="text-3xl font-bold text-black">Monarch Maintenance Agreement</h2>
          <button
            onClick={onClose}
            className="text-black hover:text-gray-700 transition-all"
          >
            <X className="w-8 h-8" />
          </button>
        </div>

        <div className="p-8 space-y-6 text-gray-700">
          <p className="font-semibold text-lg">Terms and Conditions</p>
          
          <div className="space-y-4">
            <div>
              <p className="font-semibold">1. Program Commitment</p>
              <p>By enrolling in the Monarch Maintenance Program, you agree to a minimum 6-month commitment. This commitment ensures consistent care and allows us to maintain your vehicle at peak condition.</p>
            </div>

            <div>
              <p className="font-semibold">2. Initial Deep Clean Requirement</p>
              <p>All vehicles must undergo the Alchemist Deep Clean before beginning monthly maintenance. This establishes the baseline condition we will maintain throughout your membership.</p>
            </div>

            <div>
              <p className="font-semibold">3. Monthly Maintenance Schedule</p>
              <p>You will receive a dedicated weekly time slot for your monthly maintenance detail. This is your standing appointment and should be treated with the same importance as any professional commitment.</p>
            </div>

            <div>
              <p className="font-semibold">4. Payment Terms</p>
              <p>Monthly maintenance fees are due at the beginning of each month. The initial $50 deposit will be applied to your Alchemist Deep Clean service.</p>
            </div>

            <div>
              <p className="font-semibold">5. Cancellation Policy</p>
              <p>Appointments must be rescheduled with at least 48 hours notice. Last-minute cancellations may result in forfeiture of that month's service without refund.</p>
            </div>

            <div>
              <p className="font-semibold">6. Vehicle Accessibility</p>
              <p>You agree to provide reasonable access to your vehicle at the scheduled time, whether at your home or work address. Keys or access instructions must be provided in advance when necessary.</p>
            </div>

            <div>
              <p className="font-semibold">7. Termination</p>
              <p>After the initial 6-month commitment, either party may terminate this agreement with 30 days written notice. Early termination within the first 6 months requires payment of remaining monthly fees.</p>
            </div>

            <div>
              <p className="font-semibold">8. Quality Guarantee</p>
              <p>We stand behind our work. If you're not satisfied with any service, notify us within 48 hours and we will make it right.</p>
            </div>

            <div>
              <p className="font-semibold">9. Communication</p>
              <p>You agree to maintain open communication regarding schedule changes, service concerns, or special requests. We expect timely responses to our communications as well.</p>
            </div>

            <div>
              <p className="font-semibold">10. Respect and Professionalism</p>
              <p>This is a partnership built on mutual respect. We expect courteous treatment of our team members and their expertise.</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-8 py-6 border-t-2 border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#68caf1] text-black px-10 py-3 rounded-lg font-bold hover:bg-[#5bb8db] transition-all"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// ONE-TIME DETAIL PAGE — /detail
// ==========================================
const OneTimeDetailPage = () => {
  // Ad traffic arrives with ?promo=... — carry it through the package picker
  // so a code never gets dropped between the landing page and checkout.
  const [searchParams] = useSearchParams();
  const promo = searchParams.get('promo');
  const seatPromo = searchParams.get('seatpromo');
  const addons = searchParams.get('addons');
  const bookingQuery = (packageId) => {
    const q = new URLSearchParams({ package: packageId });
    if (promo) q.set('promo', promo);
    if (seatPromo) q.set('seatpromo', seatPromo);
    if (addons) q.set('addons', addons);
    return q.toString();
  };
  return (
  <div className="min-h-screen">
    {/* Hero */}
    <section className="text-[#111827] py-20 px-6 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="animate-orbPulse absolute top-[-60px] right-[-60px] w-[300px] h-[300px] rounded-full bg-[#68caf1] blur-[100px] opacity-10" />
      </div>
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <p className="animate-fadeUp text-[#68caf1] text-sm font-bold uppercase tracking-widest mb-4">
          Interior Detailing · Southern Maryland
        </p>
        <h1 className="animate-fadeUp delay-100 text-5xl md:text-7xl font-bold mb-6 leading-tight text-[#111827]">
          Your Interior,<br />
          <span className="text-[#68caf1]">Transformed.</span>
        </h1>
        <p className="animate-fadeUp delay-200 text-lg text-[#6B7280] max-w-2xl mx-auto leading-relaxed">
          Professional interior detailing, Monday through Saturday. Mobile service — we come to you anywhere in Southern Maryland.
          Pick your package, pick your date. No subscription, no commitment.
        </p>
      </div>
    </section>

    {/* How It Works */}
    <section className="py-12 px-6 border-t border-gray-200">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          {[
            { step: '1', icon: '✨', title: 'Pick Your Package', desc: 'Average, Advanced or The Alchemist — price is based on vehicle size' },
            { step: '2', icon: '📅', title: 'Choose Your Date', desc: 'Mon–Sat, 9:00 AM — pick any open day' },
            { step: '3', icon: '🚗', title: 'We Come to You', desc: '50% deposit today — mobile service at your home or job' },
          ].map(({ step, icon, title, desc }) => (
            <div key={step} className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-[#68caf1] text-black font-bold text-lg flex items-center justify-center mb-4 shadow">
                {step}
              </div>
              <div className="text-3xl mb-3">{icon}</div>
              <h3 className="font-bold text-[#111827] text-lg mb-2">{title}</h3>
              <p className="text-[#6B7280] text-sm">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Package Cards */}
    <section className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="reveal text-4xl md:text-5xl font-bold text-[#111827] mb-4">Interior Packages</h2>
          <p className="reveal text-[#6B7280] text-lg">Average from $75 · Advanced from $120 · The Alchemist from $200 — sedan pricing, SUV and truck slightly higher</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {ONE_TIME_PACKAGES.filter(p => p.id !== 'dev_test').map((pkg, idx) => (
            <div
              key={pkg.id}
              className={`reveal card-hover relative rounded-2xl p-8 flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${
                pkg.highlight
                  ? 'bg-[#68caf1]/10 text-[#111827] border-4 border-[#68caf1] animate-glow shadow-[0_0_40px_rgba(104,202,241,0.2)]'
                  : 'bg-white border border-gray-200 shadow-sm hover:border-[#68caf1]/50'
              }`}
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              {pkg.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-[#68caf1] text-black text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="mb-6">
                <span className="text-3xl mb-3 block">{pkg.emoji}</span>
                <h3 className="text-2xl font-bold mb-1 text-[#111827]">{pkg.name}</h3>
                <p className="text-sm text-[#6B7280] mb-4">{pkg.tagline}</p>
                <div className="flex items-end gap-1">
                  <span className="text-4xl font-bold text-[#111827]">${pkg.price}</span>
                  <span className="text-[#6B7280] text-sm pb-1">/ visit</span>
                </div>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#374151]">
                    <Check className="w-4 h-4 text-[#68caf1] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                <div className="mb-8">
                  <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-2">Not included:</p>
                  <ul className="space-y-2">
                    {pkg.notIncluded.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-[#6B7280]">
                        <span className="flex-shrink-0 mt-0.5">✕</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Link
                to={`/detail/book?${bookingQuery(pkg.id)}`}
                className={`block text-center py-3 rounded-xl font-bold text-base transition-all duration-200 ${
                  pkg.highlight
                    ? 'bg-[#68caf1] text-black hover:bg-[#5bb8db]'
                    : 'bg-gray-900 text-white hover:bg-[#68caf1] hover:text-black'
                }`}
              >
                Book {pkg.name}
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-[#6B7280] mt-10">
          Need it sooner, or your day is full?{' '}
          <a
            href={`sms:${SHOP_PHONE_SMS}?&body=${encodeURIComponent("Hi Akeem, I'd like to request a rush / same-day detail.")}`}
            onClick={() => trackEvent('Contact', { content_name: 'rush_request', content_category: 'detail_page' })}
            className="font-bold text-[#0e87b3] hover:underline"
          >
            Text for a rush (+${RUSH_FEE})
          </a>
          {' '}— Akeem confirms by text, based on his schedule.
        </p>
      </div>
    </section>
  </div>
  );
};

// PHASE 2: BookingSelectionPage removed — /detail is the package picker every Book CTA points to.
// Multi-service booking selection (program + weekend + detail) will return in Phase 2.

// PHASE 2: WeekendPage removed for interiors-only launch.
// Weekend tier content (Caterpillar/Butterfly/Alchemist) is preserved in main and drop-off-phase branches.

// ==========================================
// MAIN APP COMPONENT
// ==========================================
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // Ad landing pages (and the post-payment success page) skip the 3-second splash
  // — paid traffic should hit the offer instantly, and a customer returning from
  // Stripe should see their confirmation, not a splash. Landing on one of them
  // also marks the splash as seen so the rest of the visit isn't interrupted.
  const [splash, setSplash] = useState(() => {
    if (sessionStorage.getItem('splashSeen')) return false;
    if (['/promo', '/book-now', '/detail/success', '/halloween', '/flyer'].includes(window.location.pathname)) {
      sessionStorage.setItem('splashSeen', '1');
      return false;
    }
    return true;
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [, setActivePage] = useState('program');
  const [, setCurrentStep] = useState(1);
  const [showAgreement, setShowAgreement] = useState(false);

  // Track which slots are already booked
const [isLoadingSlots, setIsLoadingSlots] = useState(true); // eslint-disable-line no-unused-vars

  // Real-time spot counter — live count from enrollments collection
  const TOTAL_SPOTS = 20;
  const [spotsLeft, setSpotsLeft] = useState(null); // null = loading

  useEffect(() => {
    supabase.rpc('get_enrollment_count')
      .then(({ data: count, error }) => {
        if (error) { setSpotsLeft(TOTAL_SPOTS); return; }
        setSpotsLeft(Math.max(0, TOTAL_SPOTS - (count || 0)));
      });
  }, []);

  // HighLevel chat widget — the loader script itself is static in
  // public/index.html (HighLevel's own compliance checker only reads raw
  // HTML, so a JS-injected script tag is invisible to it). To keep the
  // widget from ever actually appearing alongside a phone/SMS-consent
  // form (booking wizards, the raffle, the quiz), hide its rendered UI
  // via CSS on those routes — the script stays loaded, it just never
  // shows anything there.
  useEffect(() => {
    const HL_HIDE_STYLE_ID = 'hl-chat-widget-hide';
    const PHONE_COLLECTING_PREFIXES = [
      '/enter', '/win', '/interior-check', '/halloween', '/flyer',
      '/maintenance/book', '/weekend/book', '/detail/book',
      '/admin',
    ];
    const isPhoneCollectingPage = PHONE_COLLECTING_PREFIXES.some(
      (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)
    );

    let styleTag = document.getElementById(HL_HIDE_STYLE_ID);

    if (isPhoneCollectingPage) {
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = HL_HIDE_STYLE_ID;
        styleTag.textContent = `
          iframe[src*="leadconnectorhq.com"],
          #chat-widget, #chat-widget-container, .lc_text-widget, #lc_text_widget {
            display: none !important;
            visibility: hidden !important;
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(styleTag);
      }
    } else if (styleTag) {
      styleTag.remove();
    }
  }, [location.pathname]);

  // Scroll-reveal: watch all .reveal elements and add .visible when they enter the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    const revealEls = document.querySelectorAll('.reveal');
    revealEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  });


  // Meta Pixel + ad attribution. Attribution (fbclid/UTMs) is captured before
  // anything else so a fast bounce still keeps the ad click; PageView fires on
  // every client-side route change (admin and invoice-pay pages are skipped).
  // All no-ops until REACT_APP_META_PIXEL_ID is set.
  useEffect(() => {
    captureAttribution();
    initMetaPixel();
  }, []);
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // If navigating to /home with a specific section in state, activate it
  useEffect(() => {
    if (location.pathname === '/home' && location.state?.activePage) {
      setActivePage(location.state.activePage);
    }
  }, [location.pathname, location.state]);

  if (splash) {
    return <SplashScreen onDone={() => { sessionStorage.setItem('splashSeen', '1'); setSplash(false); }} />;
  }

  // Ad landing pages carry their own logo + CTA — no site nav to leak clicks away from the offer.
  const isAdLanding = ['/promo', '/book-now', '/halloween', '/flyer'].includes(location.pathname);
  const lightPages = ['/', '/about', '/education', '/transformations', '/process', '/weekend', '/detail'];
  const isLightPage = lightPages.some(p => location.pathname === p);

  return (
    <div className={`min-h-screen font-sans ${isLightPage ? 'bg-cloud text-[#111827]' : 'bg-black text-white'}`}>
      <nav className={`${isAdLanding ? 'hidden ' : ''}sticky top-0 z-50 backdrop-blur-md border-b transition-colors duration-300 bg-black/90 border-[#68caf1]/20 shadow-[0_4px_24px_rgba(0,0,0,0.4)]`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 cursor-pointer group" onClick={() => navigate('/')}>
              <img src="/images/logo.png" alt="Monarch" className="h-14 w-auto object-contain transition-all duration-300 drop-shadow-[0_0_8px_rgba(104,202,241,0.5)] group-hover:drop-shadow-[0_0_16px_rgba(104,202,241,0.8)]" />
              <div>
                <div className="font-bold text-xl tracking-wide text-white">MONARCH DETAILING</div>
                <div className="text-[#68caf1] text-xs tracking-widest">AUTOMOTIVE ALCHEMY</div>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/about" className={`font-medium transition-colors duration-200 hover:text-[#68caf1] ${location.pathname === '/about' ? 'text-[#68caf1]' : 'text-white/80'}`}>About Monarch</Link>
              <Link to="/process" className={`font-medium transition-colors duration-200 hover:text-[#68caf1] ${location.pathname === '/process' ? 'text-[#68caf1]' : 'text-white/80'}`}>Our Process</Link>
              <Link to="/transformations" className={`font-medium transition-colors duration-200 hover:text-[#68caf1] ${location.pathname === '/transformations' ? 'text-[#68caf1]' : 'text-white/80'}`}>Transformations</Link>
              <Link to="/education" className={`font-medium transition-colors duration-200 hover:text-[#68caf1] ${location.pathname === '/education' ? 'text-[#68caf1]' : 'text-white/80'}`}>Education</Link>
              <Link
                to="/detail"
                className="bg-[#68caf1] text-black px-6 py-2 rounded-lg font-bold hover:bg-[#5bb8db] hover:shadow-[0_0_16px_rgba(104,202,241,0.4)] transition-all duration-200"
              >
                Book Now
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white">
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4 border-t pt-4 bg-black border-gray-800">
              <Link to="/about" onClick={() => setIsMenuOpen(false)} className="block w-full text-left font-medium py-2 hover:text-[#68caf1] text-white">About Monarch</Link>
              <Link to="/process" onClick={() => setIsMenuOpen(false)} className="block w-full text-left font-medium py-2 hover:text-[#68caf1] text-white">Our Process</Link>
              <Link to="/transformations" onClick={() => setIsMenuOpen(false)} className="block w-full text-left font-medium py-2 hover:text-[#68caf1] text-white">Transformations</Link>
              <Link to="/education" onClick={() => setIsMenuOpen(false)} className="block w-full text-left font-medium py-2 hover:text-[#68caf1] text-white">Education</Link>

              {/* MOBILE BOOK NOW */}
              <Link
                to="/detail"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full bg-[#68caf1] text-black px-6 py-3 rounded-lg font-bold text-center"
              >
                Book Now
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Pages Rendered Here */}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/book" element={<Navigate to="/detail" replace />} />
          <Route path="/program" element={<Navigate to="/" replace />} />
          <Route path="/apply" element={<Navigate to="/" replace />} />
          <Route path="/success" element={<SuccessPage />} />
          {/* PHASE 2: <Route path="/weekend" element={<WeekendPage />} /> */}
          {/* PHASE 2: <Route path="/weekend/book" element={<WeekendBookingWizard />} /> */}
          <Route path="/weekend" element={<Navigate to="/" replace />} />
          <Route path="/weekend/book" element={<Navigate to="/" replace />} />
          <Route path="/weekend/success" element={<WeekendSuccessPage />} />
          <Route path="/maintenance" element={<InteriorMaintenancePage />} />
          <Route path="/maintenance/book" element={<MaintenanceBookingWizard />} />
          <Route path="/maintenance/confirmed" element={<MaintenanceConfirmedPage />} />
          <Route path="/promo" element={<PromoPage />} />
          <Route path="/halloween" element={<HalloweenPage />} />
          <Route path="/flyer" element={<FlyerPage />} />
          <Route path="/book-now" element={<BookNowPage />} />
          <Route path="/interior-check" element={<InteriorQuizPage />} />
          <Route path="/detail" element={<OneTimeDetailPage />} />
          <Route path="/detail/book" element={<OneTimeBookingWizard />} />
          <Route path="/detail/success" element={<OneTimeSuccessPage />} />
          <Route path="/pay/:bookingId/thank-you" element={<InvoiceThankYouPage />} />
          <Route path="/pay/:bookingId" element={<InvoicePayPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/enter" element={<RaffleEntryPage />} />
          <Route path="/win" element={<RaffleEntryPage />} />
          <Route path="/education" element={<EducationPage />} />
          <Route path="/about" element={<FoundationsPage />} />
          <Route path="/process" element={<AlchemistProcessPage setActivePage={setActivePage} setCurrentStep={setCurrentStep} spotsLeft={spotsLeft} />} />
          <Route path="/transformations" element={<GalleryPage setActivePage={setActivePage} setCurrentStep={setCurrentStep} spotsLeft={spotsLeft} />} />
          {/* ─── Admin ─── */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin" element={<PrivateRoute><NewAdminLayout /></PrivateRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/:clientId" element={<ClientProfile />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="bookings/new" element={<AdminBookingWizard />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="payments" element={<Payments />} />
            <Route path="maintenance" element={<MaintenanceProgram />} />
            <Route path="jobs/:bookingId" element={<JobDetail />} />
            <Route path="activities" element={<Activities />} />
            <Route path="winback" element={<WinBack />} />
            <Route path="raffle" element={<Raffle />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="bg-[#0a0a0a] border-t border-white/10 text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div>
              <img src="/images/logo.png" alt="Monarch Detailing" className="h-16 w-auto object-contain mb-4 opacity-90" />
              <p className="text-gray-400 text-sm leading-relaxed">
                Automotive alchemy for busy individuals, families, and businesses across Southern Maryland.
              </p>
            </div>
            {/* Quick links */}
            <div>
              <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Quick Links</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/detail" className="text-gray-400 hover:text-[#68caf1] transition-colors">Book Your Interior Detail</Link></li>
                <li><Link to="/process" className="text-gray-400 hover:text-[#68caf1] transition-colors">Our Process</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#68caf1] transition-colors">About Monarch</Link></li>
                <li><Link to="/education" className="text-gray-400 hover:text-[#68caf1] transition-colors">Education</Link></li>
              </ul>
            </div>
            {/* Service area */}
            <div>
              <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Service Area</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#68caf1]" /> St. Mary's County, MD</li>
                <li className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#68caf1]" /> Charles County, MD</li>
                <li className="flex items-center gap-2"><MapPin className="w-3 h-3 text-[#68caf1]" /> Calvert County, MD</li>
              </ul>
              <p className="text-gray-600 text-xs mt-4">Fully mobile · We come to you</p>
            </div>
            {/* Contact */}
            <div>
              <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Contact</p>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="tel:+13018859767" className="flex items-center gap-2 text-gray-400 hover:text-[#68caf1] transition-colors">
                    <Phone className="w-3 h-3 text-[#68caf1] shrink-0" /> (301) 885-9767
                  </a>
                </li>
                <li>
                  <a href="mailto:info.monarchdetailing@gmail.com" className="flex items-center gap-2 text-gray-400 hover:text-[#68caf1] transition-colors break-all">
                    <Mail className="w-3 h-3 text-[#68caf1] shrink-0" /> info.monarchdetailing@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <Clock className="w-3 h-3 text-[#68caf1] shrink-0" /> By Appointment Only
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">&copy; 2026 Monarch Detailing. All rights reserved.</p>
            <p className="text-gray-600 text-sm">Founded by{' '}
              <a href="/admin" className="relative group text-gray-500 hover:text-[#68caf1] transition-colors no-underline">
                Akeem James
              </a>
            </p>
          </div>
        </div>
      </footer>
      
      <AgreementModal
        isOpen={showAgreement}
        onClose={() => setShowAgreement(false)}
      />
    </div>
  );
}

export default App;